/**
 * Scroll-to-first-error helper for forms.
 *
 * Plexus UI does not set `aria-invalid` or any stable selector on errored
 * inputs, so we cannot reliably detect errors from the DOM. Instead, forms
 * already know which field is in error in code, so callers pass an ordered
 * list of fields (top-to-bottom) and we scroll to the first one whose
 * `hasError` is true, located by its `data-testid`.
 */

export interface ErrorField {
  /** Whether this field is currently in an error state. */
  hasError: boolean;
  /** The `data-testid` of the field's input/control, used to locate it. */
  testId: string;
}

/**
 * Scroll the viewport to the first field in error.
 *
 * @param fields - Fields in top-to-bottom DOM order.
 * @returns The testId of the field scrolled to, or undefined if none errored.
 */
export function scrollToFirstError(fields: ErrorField[]): string | undefined {
  const firstError = fields.find((field) => field.hasError);
  if (!firstError) {
    return undefined;
  }

  // Defer to the next frame so error states have rendered before we measure.
  requestAnimationFrame(() => {
    const element = document.querySelector(
      `[data-testid="${firstError.testId}"]`
    );
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  });

  return firstError.testId;
}
